import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  WaveCloudAPI,
  mergeChatMessage,
  notifyWaveChatMentionPush,
  parseWaveChatMessageDto,
  unreadChatCount,
  type WaveChatMessage,
  type WaveChatReadState,
  type WaveParticipantRecord,
} from "@vida/wave-core";
import type { WaveSyncEngine } from "@vida/wave-core";
import { getPublicBaseUrl } from "../config";
import { createWaveApiConfig } from "../services/waveSession";

export type WaveChatLayout = "compact" | "expanded";

type Options = {
  waveId: string;
  waveTitle: string;
  syncEngine: WaveSyncEngine | null;
  currentUserSub: string;
  participants: WaveParticipantRecord[];
  open: boolean;
};

function mergeChatMessages(
  prev: WaveChatMessage[],
  incoming: WaveChatMessage[],
): WaveChatMessage[] {
  let next = prev;
  for (const item of incoming) {
    next = mergeChatMessage(next, item);
  }
  return next;
}

export function useWaveChat({
  waveId,
  waveTitle,
  syncEngine,
  currentUserSub,
  participants,
  open,
}: Options) {
  const [messages, setMessages] = useState<WaveChatMessage[]>([]);
  const [readState, setReadState] = useState<WaveChatReadState | null>(null);
  const [loading, setLoading] = useState(false);
  const [threadParentId, setThreadParentId] = useState<string | null>(null);
  const [threadReplies, setThreadReplies] = useState<WaveChatMessage[]>([]);
  const [layout, setLayout] = useState<WaveChatLayout>("compact");
  const [toast, setToast] = useState<WaveChatMessage | null>(null);
  const openRef = useRef(open);
  const threadParentIdRef = useRef<string | null>(null);
  openRef.current = open;
  threadParentIdRef.current = threadParentId;

  const mergeIncoming = useCallback((incoming: WaveChatMessage) => {
    setMessages((prev) => mergeChatMessage(prev, incoming));
    const parentId = threadParentIdRef.current;
    if (parentId && incoming.parentId === parentId) {
      setThreadReplies((prev) => mergeChatMessage(prev, incoming));
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!waveId) return;
    setLoading(true);
    try {
      const cfg = createWaveApiConfig();
      const [conn, rs] = await Promise.all([
        WaveCloudAPI.listChatMessages(cfg, waveId, 80),
        WaveCloudAPI.getChatReadState(cfg, waveId),
      ]);
      setMessages((prev) => mergeChatMessages(prev, conn.items));
      setReadState(rs);
    } catch (err) {
      console.warn("[Wave chat] refresh failed", err);
    } finally {
      setLoading(false);
    }
  }, [waveId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!syncEngine || !waveId) return;
    const unsubMsg = syncEngine.subscribeChatMessage(waveId, (raw) => {
      const incoming = parseWaveChatMessageDto(raw as unknown as Record<string, unknown>);
      if (!incoming.messageId) return;
      mergeIncoming(incoming);
      if (!openRef.current && incoming.authorSub !== currentUserSub) {
        setToast(incoming);
      }
    });
    const unsubRxn = syncEngine.subscribeChatReaction(waveId, (raw) => {
      const updated = parseWaveChatMessageDto(raw as unknown as Record<string, unknown>);
      if (!updated.messageId) return;
      mergeIncoming(updated);
    });
    return () => {
      unsubMsg();
      unsubRxn();
    };
  }, [syncEngine, waveId, currentUserSub, mergeIncoming]);

  useEffect(() => {
    if (!open) return;
    const roots = messages.filter((m) => !m.parentId);
    const latest = roots.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)).at(-1);
    if (!latest) return;
    const cfg = createWaveApiConfig();
    void WaveCloudAPI.markChatRead(cfg, {
      waveId,
      lastReadMessageId: latest.messageId,
      lastReadAt: latest.createdAt,
    }).then(setReadState);
  }, [open, messages, waveId]);

  const unread = useMemo(
    () => unreadChatCount(messages, readState?.lastReadAt, currentUserSub),
    [messages, readState?.lastReadAt, currentUserSub],
  );

  const postMessage = useCallback(
    async (body: string, parentId?: string | null) => {
      const cfg = createWaveApiConfig();
      try {
        const msg = await WaveCloudAPI.postChatMessage(cfg, {
          waveId,
          body,
          parentId: parentId ?? null,
          clientMessageId: crypto.randomUUID(),
        });
        mergeIncoming(msg);
        void notifyWaveChatMentionPush(cfg, {
          waveId,
          waveTitle,
          body,
          authorLabel: msg.authorName ?? "Un partecipante",
          participants,
          currentUserSub,
          spaBase: getPublicBaseUrl() || undefined,
        });
        window.setTimeout(() => {
          void refresh();
        }, 400);
        return msg;
      } catch (err) {
        console.warn("[Wave chat] postMessage failed", err);
        throw err;
      }
    },
    [waveId, waveTitle, participants, currentUserSub, mergeIncoming, refresh],
  );

  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      const cfg = createWaveApiConfig();
      const updated = await WaveCloudAPI.toggleChatReaction(cfg, waveId, messageId, emoji);
      mergeIncoming(updated);
    },
    [waveId, mergeIncoming],
  );

  const openThread = useCallback(
    async (parentId: string) => {
      setThreadParentId(parentId);
      const cfg = createWaveApiConfig();
      const conn = await WaveCloudAPI.listChatReplies(cfg, waveId, parentId, 80);
      setThreadReplies((prev) => mergeChatMessages(prev, conn.items));
    },
    [waveId],
  );

  const closeThread = useCallback(() => {
    setThreadParentId(null);
    setThreadReplies([]);
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  return {
    messages,
    threadParentId,
    threadReplies,
    participants,
    loading,
    unread,
    layout,
    setLayout,
    toast,
    dismissToast,
    refresh,
    postMessage,
    toggleReaction,
    openThread,
    closeThread,
  };
}
